// src/context/ContactModalContext.tsx
// Ownership of the Contact window.
//
// The Navbar and the Footer are siblings under the router, not parent/child, so
// neither can receive the other's open handler as a prop without threading it
// through App -> AppShell -> Routes -> page -> Footer. This provider is the
// nearest common parent, and it is the smallest thing that solves the problem:
// two `useState` lines, no store, no library.
//
// It deliberately mirrors src/context/QueryModalContext.tsx, which does the same
// job for the query window, so the codebase has one way of sharing app-level
// modal state. The two are separate providers on purpose: the forms collect
// different data and post different emails, and merging them would make
// "openContactModal" mean "pick one for me".
//
// The single <ContactModal> instance is rendered here rather than by each
// caller. ModalBox portals it to document.body, so where it sits in the tree
// does not affect layout or stacking -- mounting it in two places would have
// produced two dialogs competing for the same focus trap and scroll lock.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import ContactModal from '../components/Contact/ContactModal';

interface ContactModalContextValue {
  /** Whether the Contact window is open. Read only if a surface must react to it. */
  isContactModalOpen: boolean;
  /** Opens the window. Safe to call from any surface. */
  openContactModal: () => void;
  /** Closes the window. */
  closeContactModal: () => void;
}

const ContactModalContext = createContext<ContactModalContextValue | undefined>(undefined);

export const ContactModalProvider = ({ children }: { children: ReactNode }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Stable identities: these are passed to the memoised value below, and the
  // modal's own close paths are wired to them directly.
  const openContactModal = useCallback(() => setIsContactModalOpen(true), []);
  const closeContactModal = useCallback(() => setIsContactModalOpen(false), []);

  const value = useMemo(
    () => ({ isContactModalOpen, openContactModal, closeContactModal }),
    [isContactModalOpen, openContactModal, closeContactModal]
  );

  return (
    <ContactModalContext.Provider value={value}>
      {children}
      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
    </ContactModalContext.Provider>
  );
};

// Custom hook
export const useContactModal = () => {
  const context = useContext(ContactModalContext);
  if (!context) throw new Error('useContactModal must be used within ContactModalProvider');
  return context;
};
